import { useCallback } from 'react';

import { StoryBoardTracks } from 'services/storyboard.service';
import { LogLevel } from 'services/logger.service';
import { RootState } from 'store/store';
import { useAppDispatch, useAppStore } from 'store/store-hooks';
import { isNull } from 'components/app/_helpers/_app.helper';
import { useDraggerContext } from 'components/app/_mediator/app-dragger.provider';
import { appLogger } from 'components/app/_mediator/app-logger.slice';
import { useAudioTrackContext } from 'components/app/audio-track/_mediator/audio-track.provider';
import { useSbPlayerContext } from 'components/app/storyboard/sb-player/_mediator/sb-player.provider';
import { IdleAction, useSbIdle } from 'components/app/storyboard/sb-player/_mediator/use-sb-player-idle';
import { EnumFader, useSbPlayerCb } from 'components/app/storyboard/sb-player/_mediator/use-sb-player-cb';
import { setSbLoaderListener } from './sb.slice';
import { useSbContext } from './sb.provider';

export type SbPlay = (players: Index[] | null, action: IdleAction) => void;

export const useSbCb = () => {
  const { sbListenClip } = useSbContext();
  const { sbPlayerPause, sbPlayerPlay } = useSbPlayerContext();
  const { ws, wsSeekTo, wsPlay, wsGetCursorTime } = useAudioTrackContext();
  const { handleIdle, resetIdle } = useSbIdle();
  const { isDragging } = useDraggerContext();
  const { toggleFader } = useSbPlayerCb();
  const dispatch = useAppDispatch();
  const store = useAppStore();

  const sbSelectClip = useCallback((clipId: CellClip, cell: Index, track: StoryBoardTracks) => {
    const { markers, storyBoard, sbWsPlayback } = selectData(store.getState());
    if (!ws.current || !markers) return;
    if (storyBoard[track][cell] !== clipId) {
      console.error('useSbCb: sbSelectClip: the clipId does not match');
      return;
    }
    if (!sbWsPlayback) sbListenClip.current = { clipId, cell, track };
    sbSeekTo(cell === 0 ? 0 : markers[cell - 1]);
    toggleFader(EnumFader.off);
  }, []);

  const sbPlay: SbPlay = useCallback((players, action) => {
    if (isNull(players)) {
      sbPause();
      return;
    }
    sbPlayerPlay(handleIdle(players, action, sbPlay));
    wsPlay(true);
  }, []);

  const sbPause = useCallback(() => {
    resetIdle();
    wsPlay(false);
    sbPlayerPause();
  }, []);

  const sbSeekTo = useCallback((value: SeekToValue, type: SeekToType = 'seconds') => {
    if (value < 0) {
      dispatch(
        appLogger({
          level: LogLevel.Fatal,
          error: { name: 'sbSeekTo', message: 'value is less than 0' },
        })
      );
      return;
    }
    if (!isDragging.current) dispatch(setSbLoaderListener());
    wsSeekTo(value, type);
  }, []);

  const sbSync = useCallback(() => {
    // video and audio sync (ws<—>player)
    if (!selectData(store.getState()).tracksRestored) return;
    wsSeekTo(wsGetCursorTime());
  }, []);

  return { sbPause, sbPlay, sbSelectClip, sbSeekTo, sbSync };
};

/* S E L E C T O R */

const selectData = (state: RootState) => {
  const { storyBoard, markers } = state.editor;
  const { tracksRestored } = state.memory;
  const { sbWsPlayback } = state.sb;
  return { storyBoard, markers, tracksRestored, sbWsPlayback };
};
